// AIClientVision — the live session view.
// Browser-side MediaPipe drives the vision pipeline (pose + segmentation +
// auto-calibration + position classification + temporal smoothing + baseline
// tracking). The server returns aero physics. This component renders the
// video feed with pose overlay on the left and a live stats sidebar on the
// right showing smoothed CdA, baseline/delta, watts saved, segment breakdown,
// and calibration health.

import { fmtSpeed, useUnitSystem } from './services/useUnitSystem';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  ThemeProvider,
  Tooltip,
  Typography,
} from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import StopIcon from '@mui/icons-material/Stop';
import StopCircleIcon from '@mui/icons-material/StopCircle';
import GestureActionBar from './components/GestureActionBar';
import BoltIcon from '@mui/icons-material/Bolt';
import { useTranslation } from 'react-i18next';
import { isRaphaDomain } from './services/subdomain';
import VisionOverlay, { VisionOverlayModes } from './components/VisionOverlay';
import MeshCanvas from './components/MeshCanvas';
import VisualizationToolbar from './components/VisualizationToolbar';
import StatCard from './components/StatCard';
import Stream from './components/Stream';
import AeroAiPanel from './components/AeroAiPanel';
import { Voice } from './services/voice';
import AvatarPanel from './components/AvatarPanel';
import AvatarOverlay from './components/AvatarOverlay';
import PressureMesh from './components/PressureMesh';
import SilhouetteMesh from './components/SilhouetteMesh';
import YawSweepPrompt from './components/YawSweepPrompt';
import { YawSweep, estimateHeadYawDeg, estimateBodyYawDeg, YawSweepPhase } from './vision/yawSweep';
import { computeRegionVisibility } from './vision/regionVisibility';
import { computeRegionOcclusion } from './vision/regionOcclusion';
import { computePressureWeightedShares } from './vision/pressureWeightedShares';
import { HandDetector, type DetectedHand } from './vision/handDetector';
import { bodyPartFramesFromLandmarks } from './vision/cylinderBody';
import * as THREE from 'three';
import SessionReport from './components/SessionReport';
import PaywallModal from './components/PaywallModal';
import UsageChip from './components/UsageChip';
import { updateUsageFromHeartbeat } from './services/useSubscription';
import PositionComparison from './components/PositionComparison';
import BluetoothPanel from './components/BluetoothPanel';
import { Boards } from './enums';
import { useVisionPipeline } from './hooks/useVisionPipeline';
import { useBluetooth } from './hooks/useBluetooth';
import { usePositionsAnalytics } from './hooks/usePositionsAnalytics';
import { labelForPosition } from './vision/positionClassifier';
import { colors } from './theme';
import CloseIcon from '@mui/icons-material/Close';
import OnboardingWalkthrough from './components/OnboardingWalkthrough';
import { readCurrentUser } from './services/useCurrentUser';

// v6: drag heatmap added (Track 2.2), defaults ON since it's the new
// hero overlay. The previous v5 segmentation layer becomes a "show me
// which-pixel-is-what" diagnostic toggle and stays off by default.
const VIZ_STORAGE_KEY = 'aerochat_viz_modes_v6';
const DEFAULT_MODES: VisionOverlayModes = {
  skeleton: true,
  contour: true,
  segmentation: false,
  dragHeatmap: true,
  mesh: false,
  pose3d: true,
  videoOff: false,
  rotation: 0,
};

function loadVizModes(): VisionOverlayModes {
  try {
    const raw = localStorage.getItem(VIZ_STORAGE_KEY);
    if (!raw) return DEFAULT_MODES;
    const parsed = JSON.parse(raw);
    return { ...DEFAULT_MODES, ...parsed };
  } catch {
    return DEFAULT_MODES;
  }
}

// Calibration labels are resolved via i18n at render time — see getCalLabel() below.

export default function AIClientVision(props: {
  theme: any;
  dashboards: Boards[];
  // Wave 20.44 — position the rider chose on /session/focus. 'new'
  // when recording a not-yet-known position; a class string when
  // refining an existing one; null when they came in the old way.
  focusClass?: string | null;
  onEndSession?: () => void;
  // Wave 20.16 fix — parent may hold an outer End Session button
  // (e.g. Dashboard's bottom-right button); it needs to call the
  // same persist-and-report handler this component owns, otherwise
  // the row never lands in session_summary and the session doesn't
  // show up in the feed. Assign our end handler here on mount.
  endSessionRef?: React.MutableRefObject<(() => void) | null>;
}) {
  const units = useUnitSystem();
  const { t, i18n } = useTranslation('session');
  const videoRef = useRef<HTMLVideoElement>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const streamDataRef = useRef<Array<{ date: Date; value: number }>>([]);
  // Track 3 — Per-second time-series captured for the SessionDetail
  // dashboard. CdA + position + sensors sampled at ~1 Hz from session
  // start; posted with the session summary on End Session.
  const sessionStartedAtRef = useRef<number | null>(null);
  const timeSeriesRef = useRef<
    Array<{
      t: number;
      cda?: number;
      drag_power_w?: number;
      position?: string;
      power_w?: number;
      hr_bpm?: number;
      cadence_rpm?: number;
    }>
  >([]);
  const lastTimeSeriesSampleRef = useRef<number>(0);
  // Wave 13 / Track B — running sum of per-region visibility so we can
  // emit `regionVisibilityHighlights` (mean across the whole session)
  // in the End Session POST without keeping every sample around.
  const visibilityAccRef = useRef<{
    head: number;
    torso: number;
    arms: number;
    legs: number;
    count: number;
  }>({ head: 0, torso: 0, arms: 0, legs: 0, count: 0 });
  // Wave 13 / Track F — running sum of Cp(θ)-weighted area shares per
  // region. Combined with visibilityAccRef on the server side: the
  // mesh's integrated pressure becomes a real input to drag attribution.
  const pressureSharesAccRef = useRef<{
    head: number;
    torso: number;
    arms: number;
    legs: number;
    count: number;
  }>({ head: 0, torso: 0, arms: 0, legs: 0, count: 0 });
  const [, forceRerender] = useState(0);
  const [vizModes, setVizModes] = useState<VisionOverlayModes>(loadVizModes);
  const [fullscreen, setFullscreen] = useState(false);
  // Wave 7 — SMPL fit now comes from the client-side pipeline (derived from
  // MediaPipe world landmarks), not the vision-advanced WebSocket.
  // Wave 6.5 — per-rider learned vision correction factor. Fetched once
  // on mount; applied as a multiplier on the displayed CdA so riders who
  // have logged paired power+vision sessions see calibrated numbers.
  // Wave 13 / Track D — riders without paired power data inherit the
  // population mean on profile creation. `visionCorrectionSource` lets the
  // UI label which one is active ('personal' vs 'population').
  const [visionCorrection, setVisionCorrection] = useState<number>(1.0);
  const [visionCorrectionSource, setVisionCorrectionSource] = useState<
    'personal' | 'population' | 'none'
  >('none');
  useEffect(() => {
    const apiBase = process.env.REACT_APP_API || '';
    const token = localStorage.getItem(
      process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token',
    );
    if (!apiBase || !token) return;
    fetch(`${apiBase}/sessions/me/profile`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((p) => {
        if (!p || !(p.visionCorrectionFactor > 0)) return;
        setVisionCorrection(p.visionCorrectionFactor);
        if (p.visionCorrectionSamples >= 3) {
          setVisionCorrectionSource('personal');
        } else if (Math.abs(p.visionCorrectionFactor - 1.0) > 0.005) {
          setVisionCorrectionSource('population');
        } else {
          setVisionCorrectionSource('none');
        }
      })
      .catch(() => {});
  }, []);

  // Wave 15 — session.started webhook. Fires once when the vision
  // session begins (component mount). Server threads all subsequent
  // session-scoped events under this parent Slack message.
  //
  // Wave 16 — also pipes usage into the shared subscription cache each
  // heartbeat (updateUsageFromHeartbeat) so the HUD chip + dashboard
  // card stay live without a separate poll. Free users get 402 on start
  // when their weekly cap is exhausted → paywall modal opens.
  const clientSessionIdRef = useRef<string>(
    typeof crypto !== 'undefined' && (crypto as any).randomUUID
      ? (crypto as any).randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  );
  const bestInSessionCdaRef = useRef<number | null>(null);
  const lastReportedElapsedSecRef = useRef<number>(0);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallReason, setPaywallReason] = useState<
    'weekly_cap' | 'locked_feature'
  >('weekly_cap');
  // Wave 18 — first-time onboarding walkthrough. Loads user settings
  // once on mount; if hasCompletedOnboarding !== true, activates the
  // OnboardingWalkthrough overlay. On complete, PUTs the flag back to
  // true so the tour doesn't re-run next session unless the rider
  // explicitly re-enables it from Account. Preserves other settings
  // (sendMarketingEmails) since the jsonb column is replaced whole.
  const [showOnboarding, setShowOnboarding] = useState(false);
  const userSettingsRef = useRef<Record<string, any>>({});
  useEffect(() => {
    const apiBase = process.env.REACT_APP_API || '';
    const token = localStorage.getItem(
      process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token',
    );
    if (!apiBase || !token) return;
    fetch(`${apiBase}/users`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((u: { settings?: Record<string, any> } | null) => {
        if (!u) return;
        userSettingsRef.current = u.settings ?? {};
        if (u.settings?.hasCompletedOnboarding !== true) {
          setShowOnboarding(true);
        }
      })
      .catch(() => {});
  }, []);
  const completeOnboarding = useCallback(() => {
    setShowOnboarding(false);
    const apiBase = process.env.REACT_APP_API || '';
    const token = localStorage.getItem(
      process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token',
    );
    if (!apiBase || !token) return;
    const nextSettings = {
      ...userSettingsRef.current,
      hasCompletedOnboarding: true,
    };
    userSettingsRef.current = nextSettings;
    fetch(`${apiBase}/users/`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ settings: nextSettings }),
    }).catch(() => {});
  }, []);
  useEffect(() => {
    const apiBase = process.env.REACT_APP_API || '';
    const token = localStorage.getItem(
      process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token',
    );
    if (!apiBase || !token) return;
    fetch(`${apiBase}/sessions/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        sessionId: clientSessionIdRef.current,
        source: isRaphaDomain() ? 'rapha' : 'public',
        focusClass: props.focusClass ?? undefined,
      }),
    })
      .then((r) => {
        if (r.status === 402) {
          setPaywallReason('weekly_cap');
          setPaywallOpen(true);
        }
      })
      .catch(() => {});

    const heartbeat = setInterval(() => {
      const apiBase2 = process.env.REACT_APP_API || '';
      const token2 = localStorage.getItem(
        process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token',
      );
      if (!apiBase2 || !token2) return;
      const cda = pipeline.smoothed?.cda ?? null;
      if (cda != null && cda > 0) {
        const prev = bestInSessionCdaRef.current;
        if (prev == null || cda < prev) bestInSessionCdaRef.current = cda;
      }
      const startedAt = sessionStartedAtRef.current;
      const elapsedSec =
        startedAt != null
          ? Math.max(0, Math.round((performance.now() - startedAt) / 1000))
          : null;
      const lastReportedElapsedSec = lastReportedElapsedSecRef.current;
      fetch(`${apiBase2}/sessions/heartbeat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token2}`,
        },
        body: JSON.stringify({
          sessionId: clientSessionIdRef.current,
          elapsedSec,
          lastReportedElapsedSec,
          currentCda: cda,
          currentPosition: pipeline.position?.class ?? null,
          baselineLocked: !!pipeline.baseline?.isLocked,
          calibrationUnusableForMs: pipeline.calibrationUnusableForMs ?? 0,
          bestInSessionCda: bestInSessionCdaRef.current,
        }),
      })
        .then((r) => (r.ok ? r.json() : null))
        .then((body: any) => {
          if (!body) return;
          if (elapsedSec != null) {
            lastReportedElapsedSecRef.current = elapsedSec;
          }
          if (body.usage) {
            updateUsageFromHeartbeat(body.usage);
          }
          if (body.blocked) {
            setPaywallReason('weekly_cap');
            setPaywallOpen(true);
          }
        })
        .catch(() => {});
    }, 60_000);
    return () => clearInterval(heartbeat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Track 5.1 — yaw sweep protocol state machine.
  const yawSweepRef = useRef(new YawSweep());
  const [yawPhase, setYawPhase] = useState<YawSweepPhase>('idle');
  const [yawPrompt, setYawPrompt] = useState<string | null>(null);
  const [yawCdaAt10, setYawCdaAt10] = useState<number | null>(null);
  // Wave 12 — after the yaw sweep completes, the captured keyframes go
  // through visual hull carving to produce the rider's 3D head mesh.
  // Stored once in state and re-rendered by SilhouetteMesh.
  const [headHullMesh, setHeadHullMesh] = useState<{
    positions: Float32Array;
    indices: Uint16Array;
    normals: Float32Array;
  } | null>(null);
  // Wave 13 / Track A — same idea as headHullMesh but for the whole body.
  // Built from the body-rotation keyframes; replaces the silhouette
  // extrusion in SilhouetteMesh once present. We also keep the raw
  // hull-derived area / per-region voxel counts in a ref so the End
  // Session POST can send them to the server (sectional.py uses voxel
  // ratios as the per-region area split when present).
  const bodyHullStatsRef = useRef<{
    frontal_area_m2: number;
    side_area_m2: number;
    voxel_count: number;
    per_region_voxels: { head: number; torso: number; arms: number; legs: number };
  } | null>(null);
  const [bodyHullMesh, setBodyHullMesh] = useState<{
    positions: Float32Array;
    indices: Uint16Array;
    normals: Float32Array;
  } | null>(null);
  // Track 5.2 — session report. Opens an overlay on End Session, then
  // calls the parent's onEndSession after the report is fully rendered +
  // persisted. Closing the overlay is what actually exits the session.
  const [reportSummary, setReportSummary] = useState<Record<string, any> | null>(null);
  // Wave 20.49l — confirm gate for rider-triggered baseline recapture.
  const [recaptureOpen, setRecaptureOpen] = useState(false);
  // Wave 20.49l — outer-component Voice used for narration on
  // recapture. Voice dedupes across all instances in the tab, so
  // this shares throttling with SessionNarrator / AeroAiPanel.
  const outerVoiceRef = useRef<Voice | null>(null);
  if (outerVoiceRef.current == null) {
    outerVoiceRef.current = new Voice({
      wsUrl: process.env.REACT_APP_COACH_WS || 'wss://aero-ai.aero.chat',
      enabled: localStorage.getItem('aerochat.sessionVoiceMuted') !== '1',
    });
  }
  const [reportSessionId, setReportSessionId] = useState<string | null>(null);
  // Wave 20.35 — surfaces server errors from the end-session save so a
  // 403 (e.g. liability-version-bump) shows a proper toast instead of
  // being silently swallowed and dumped into the report component as
  // raw JSON.
  const [saveError, setSaveError] = useState<
    { message: string; code: string | null; status: number } | null
  >(null);
  // Wave 20.35 — rider can silence the "reposition camera" advisory.
  const [repositionDismissed, setRepositionDismissed] = useState(false);

  // Wave 20.44 — Opening banner that frames the session around the
  // rider's chosen focus. Shown for the first 12 s of the session (or
  // until the rider dismisses); pulls historical bestCda from the
  // positions analytics endpoint to greet the rider with their prior
  // number ("best so far: 0.24 — let's make some improvements").
  const positionsAnalytics = usePositionsAnalytics();
  const [focusBannerDismissed, setFocusBannerDismissed] = useState(false);
  useEffect(() => {
    if (!props.focusClass) return;
    const id = window.setTimeout(() => setFocusBannerDismissed(true), 12_000);
    return () => window.clearTimeout(id);
  }, [props.focusClass]);
  const focusPriorBest = React.useMemo(() => {
    if (!props.focusClass || props.focusClass === 'new') return null;
    const list = positionsAnalytics.data?.positions ?? [];
    return list.find((p) => p.class === props.focusClass) ?? null;
  }, [props.focusClass, positionsAnalytics.data]);

  // handleEndSession is declared further down (after `base`, `pipeline`,
  // `ble`, and `getAeroAiContext` are in scope) so the closure captures
  // real values, not TDZ-forbidden refs.

  // Lock body scroll and exit on Esc while fullscreen is active.
  useEffect(() => {
    if (!fullscreen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFullscreen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [fullscreen]);

  // Wave 20.49l — 'E' hotkey to end the session without a mouse
  // reach. Ignored while typing in an input/textarea/contenteditable
  // so it doesn't fight the coach chat. handleEndSession is
  // declared later in this component, so we route through a ref
  // that gets updated on every render (ref-latest pattern).
  const endHandlerRef = useRef<(() => void) | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== 'e') return;
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      const t = e.target as HTMLElement | null;
      if (t) {
        const tag = t.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || t.isContentEditable) return;
      }
      endHandlerRef.current?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const updateVizModes = (next: VisionOverlayModes) => {
    setVizModes(next);
    try {
      localStorage.setItem(VIZ_STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore quota errors */
    }
  };

  // Start camera
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1280 }, height: { ideal: 720 } },
        });
        if (!active) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current!
              .play()
              .then(() => setCameraReady(true))
              .catch((e) => setCameraError(e.message));
          };
        }
      } catch (e: any) {
        setCameraError(e.message || 'Camera access denied.');
      }
    })();

    return () => {
      active = false;
      const stream = videoRef.current?.srcObject as MediaStream | null;
      stream?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const wsUrl = process.env.REACT_APP_VISION_WS || 'ws://localhost:8543';
  const aeroAiWsUrl =
    process.env.REACT_APP_COACH_WS || 'ws://localhost:8444';

  const ble = useBluetooth();

  const pipeline = useVisionPipeline({
    videoRef,
    wsUrl,
    userId: 'local-dev',
    sessionId: 'local-session',
    getSensors: ble.getSensorData,
    enabled: cameraReady,
  });

  // Keep the latest pipeline snapshot in a ref so Aero AI can read it
  // without re-creating the WebSocket every render.
  const pipelineRef = useRef(pipeline);
  pipelineRef.current = pipeline;

  const getAeroAiContext = useCallback(() => {
    const p = pipelineRef.current;
    const s = p.smoothed;
    if (!s) return undefined;
    return {
      cda: s.cda,
      cd: s.cd,
      frontal_area_m2: s.frontal_area_m2,
      drag_force_n: s.drag_force_n,
      drag_power_w: s.drag_power_w,
      predicted_speed_kph: s.predicted_speed_kph,
      position_class: p.position?.class,
      calibration_source: p.calibration?.source,
      calibration_confidence: p.calibration?.confidence,
      segments: s.segments,
    };
  }, []);

  // Wave 7 — SMPL fit is now derived client-side inside useVisionPipeline
  // from MediaPipe world landmarks. Consume directly; no WebSocket round-trip.
  const smplFit = pipeline.smplFit;

  // Wave 17.4 — avatar visuals are on by default. The Wave 7.2 gate
  // (localStorage `aero.chat:avatarEnabled`) is retired now that the
  // Wave 17.x mesh has shipped.

  // Track 5.1 — once baseline locks, surface the yaw sweep prompt. Idle →
  // waiting on the rising edge of isLocked. Re-locking doesn't re-prompt.
  useEffect(() => {
    if (pipeline.baseline.isLocked && yawSweepRef.current.currentPhase() === 'idle') {
      setYawPhase('waiting');
    }
  }, [pipeline.baseline.isLocked]);

  // Track 5.1 — feed the sweep state machine each frame. Sample yaw from
  // the face landmarks ref; CdA from the smoothed analysis. Also feed
  // baseline-locked CdA as center samples so the parabola has a strong
  // anchor at yaw=0.
  useEffect(() => {
    const id = setInterval(() => {
      const sweep = yawSweepRef.current;
      if (sweep.currentPhase() === 'idle') return;
      const faceLm = pipeline.visualization.faceLandmarksRef.current;
      const yaw = estimateHeadYawDeg(faceLm as any);
      const cda = pipeline.smoothed?.cda ?? null;
      // Wave 13 / Track A — body yaw from world-landmark shoulder
      // foreshortening + z-depth sign. Used by the body-rotation phases.
      const lm2 = pipeline.visualization.landmarksRef?.current ?? null;
      const lmW = pipeline.visualization.worldLandmarksRef?.current ?? null;
      const bodyYaw = estimateBodyYawDeg(lm2 as any, lmW as any);
      // Center samples while waiting; the rider is in their normal forward
      // gaze, so this lands at yaw≈0 and gives the fit its strongest anchor.
      if (sweep.currentPhase() === 'waiting' && cda != null && yaw != null && Math.abs(yaw) < 5) {
        sweep.feedCenterSample(cda);
      }
      const active = sweep.update({
        now: performance.now(),
        headYawDeg: yaw,
        bodyYawDeg: bodyYaw,
        cda,
      });
      setYawPhase(sweep.currentPhase());
      setYawPrompt(sweep.currentPrompt());

      // Wave 12 — supply a silhouette snapshot whenever the sweep asks.
      // We clone the labelMap (the pipeline reuses the same buffer
      // every frame, so a live reference would be overwritten before
      // the hull carving runs).
      const needsSlot = sweep.needsKeyframe();
      if (needsSlot && yaw != null) {
        const labelMap = pipeline.visualization.labelMapRef.current;
        const mw = pipeline.visualization.maskWidthRef.current ?? 0;
        const mh = pipeline.visualization.maskHeightRef.current ?? 0;
        const pxPerM = pipeline.calibration?.px_per_m ?? 0;
        if (labelMap && mw > 0 && mh > 0 && pxPerM > 0) {
          // Compute head bbox (label==1) once at capture time so the hull
          // carving has the source-image extent of the head region.
          let minX = mw, minY = mh, maxX = -1, maxY = -1;
          for (let y = 0; y < mh; y++) {
            const rowOff = y * mw;
            for (let x = 0; x < mw; x++) {
              if (labelMap[rowOff + x] === 1) {
                if (x < minX) minX = x;
                if (y < minY) minY = y;
                if (x > maxX) maxX = x;
                if (y > maxY) maxY = y;
              }
            }
          }
          const headBboxPx: [number, number, number, number] | null =
            maxX >= 0 && maxY >= 0
              ? [minX, minY, maxX - minX + 1, maxY - minY + 1]
              : null;
          sweep.acceptKeyframe({
            yawDeg: yaw,
            labelMap: new Uint8Array(labelMap), // clone
            maskW: mw,
            maskH: mh,
            pxPerM,
            headBboxPx,
          });
        }
      }

      // Wave 13 / Track A — body keyframe capture. Same clone semantics,
      // but the bbox covers the full rider (any non-bg label) and the
      // sweep state machine tracks these separately from head keyframes.
      const needsBodySlot = sweep.needsBodyKeyframe();
      if (needsBodySlot && bodyYaw != null) {
        const labelMap = pipeline.visualization.labelMapRef.current;
        const mw = pipeline.visualization.maskWidthRef.current ?? 0;
        const mh = pipeline.visualization.maskHeightRef.current ?? 0;
        const pxPerM = pipeline.calibration?.px_per_m ?? 0;
        if (labelMap && mw > 0 && mh > 0 && pxPerM > 0) {
          let minX = mw, minY = mh, maxX = -1, maxY = -1;
          for (let y = 0; y < mh; y++) {
            const rowOff = y * mw;
            for (let x = 0; x < mw; x++) {
              if (labelMap[rowOff + x] !== 0) {
                if (x < minX) minX = x;
                if (y < minY) minY = y;
                if (x > maxX) maxX = x;
                if (y > maxY) maxY = y;
              }
            }
          }
          const bodyBboxPx: [number, number, number, number] | null =
            maxX >= 0 && maxY >= 0
              ? [minX, minY, maxX - minX + 1, maxY - minY + 1]
              : null;
          sweep.acceptBodyKeyframe({
            yawDeg: bodyYaw,
            labelMap: new Uint8Array(labelMap),
            maskW: mw,
            maskH: mh,
            pxPerM,
            headBboxPx: null,
            bodyBboxPx,
          });
        }
      }

      // Wave 12 — when the sweep finishes, kick off the visual hull
      // computation. Lazy import so the carving module doesn't bloat
      // the initial bundle when the user never runs a sweep.
      if (!active && sweep.result_()) {
        setYawCdaAt10(sweep.result_()!.cda_at_10deg_yaw);
        const keyframes = sweep.result_()!.keyframes;
        const bodyKeyframes = sweep.result_()!.bodyKeyframes ?? [];
        if (keyframes && keyframes.length >= 2 && headHullMesh == null) {
          import('./vision/visualHull').then(({ buildVisualHullHead, buildVisualHullBody }) => {
            try {
              const hull = buildVisualHullHead(keyframes);
              if (hull) setHeadHullMesh(hull);
            } catch (err) {
              // eslint-disable-next-line no-console
              console.warn('[visual-hull] head carving failed:', err);
            }
            // Wave 13 / Track A — whole-body hull. Failure falls back to
            // the silhouette extrusion in SilhouetteMesh; not a regression.
            if (bodyKeyframes.length >= 2 && bodyHullMesh == null) {
              try {
                const bodyHull = buildVisualHullBody(bodyKeyframes);
                if (bodyHull) {
                  setBodyHullMesh({
                    positions: bodyHull.positions,
                    indices: bodyHull.indices,
                    normals: bodyHull.normals,
                  });
                  bodyHullStatsRef.current = {
                    frontal_area_m2: bodyHull.frontalAreaM2,
                    side_area_m2: bodyHull.sideAreaM2,
                    voxel_count: bodyHull.voxelCount,
                    per_region_voxels: bodyHull.perRegionVoxels,
                  };
                }
              } catch (err) {
                // eslint-disable-next-line no-console
                console.warn('[visual-hull] body carving failed:', err);
              }
            }
          });
        }
      }
      void active;
    }, 80);
    return () => clearInterval(id);
  }, [pipeline.visualization.faceLandmarksRef, pipeline.smoothed]);

  // Push smoothed CdA onto the timeline chart + the time-series buffer.
  useEffect(() => {
    const a = pipeline.smoothed;
    if (!a || a.cda <= 0) return;
    const stream = [...streamDataRef.current];
    stream.push({ date: new Date(), value: a.cda });
    streamDataRef.current = stream.slice(-Math.min(stream.length, 120));
    forceRerender((x) => x + 1);

    // Track 3 — sample to time_series at ~1 Hz. Seed session start
    // timestamp on the first sample. Capped at 5000 entries to match
    // the server-side cap so we don't ever send something the server
    // truncates.
    const now = performance.now();
    if (sessionStartedAtRef.current == null) {
      sessionStartedAtRef.current = now;
    }
    if (now - lastTimeSeriesSampleRef.current >= 1000) {
      lastTimeSeriesSampleRef.current = now;
      const t = Math.round((now - (sessionStartedAtRef.current ?? now)) / 1000);
      const sensors = ble.getSensorData?.();
      // Wave 13 / Track B — per-region landmark-visibility sample. Drives
      // both the per-sample debug field and the running mean we emit in
      // regionVisibilityHighlights at session end.
      const landmarkVis = computeRegionVisibility(
        pipeline.visualization.landmarksRef.current as any,
      );
      // Wave 13 / Track B finish — software z-buffer rasterization of
      // the cylinder primitives gives true occlusion-aware shares (not
      // just per-joint confidence). Blend both: geometric mean treats
      // them as independent measurements of the same physical thing.
      const worldForOcc = pipeline.visualization.worldLandmarksRef.current;
      let occShares: { head: number; torso: number; arms: number; legs: number } | null = null;
      if (worldForOcc && worldForOcc.length >= 33) {
        try {
          const occParts = bodyPartFramesFromLandmarks(worldForOcc as any);
          // Wind = -Z (rider faces +Z, relative wind comes from -Z).
          const occ = computeRegionOcclusion(occParts, new THREE.Vector3(0, 0, -1));
          if (occ) occShares = { head: occ.head, torso: occ.torso, arms: occ.arms, legs: occ.legs };
        } catch {
          // Degenerate pose — skip this sample.
        }
      }
      // Wave 20.35 — blend switched from geometric-mean to a "trust the
      // more confident signal" max(landmark, 0.8·zbuffer). The old
      // sqrt(a*b) blend halved the effective score any time ONE channel
      // was low (e.g. a single-frame occlusion on one hand); in practice
      // the arm region hovered around 0.3 all ride even when the rider
      // was clearly in frame. Downstream drag attribution + coach
      // triggers get a much more stable "is this region present?"
      // signal from the max — and the 0.8 haircut on the z-buffer
      // keeps landmark visibility slightly authoritative.
      const vis = (landmarkVis && occShares)
        ? {
            head:  Math.max(landmarkVis.head,  occShares.head  * 0.8),
            torso: Math.max(landmarkVis.torso, occShares.torso * 0.8),
            arms:  Math.max(landmarkVis.arms,  occShares.arms  * 0.8),
            legs:  Math.max(landmarkVis.legs,  occShares.legs  * 0.8),
          }
        : landmarkVis ?? (occShares ? { ...occShares } : null);
      if (vis) {
        const acc = visibilityAccRef.current;
        acc.head += vis.head;
        acc.torso += vis.torso;
        acc.arms += vis.arms;
        acc.legs += vis.legs;
        acc.count++;
      }
      // Wave 13 / Track F — Cp(θ)-weighted area share per region. Uses
      // the same body-part frames that PressureMesh renders, so the
      // visualization and the drag math see the same numbers.
      const world = pipeline.visualization.worldLandmarksRef.current;
      if (world && world.length >= 33) {
        try {
          const parts = bodyPartFramesFromLandmarks(world as any);
          // Wind = relative wind hitting a forward-moving rider: -Z in
          // world frame (the rider faces toward +Z conventionally).
          const wind = new THREE.Vector3(0, 0, -1);
          const shares = computePressureWeightedShares(parts, wind);
          if (shares) {
            const acc = pressureSharesAccRef.current;
            acc.head += shares.head;
            acc.torso += shares.torso;
            acc.arms += shares.arms;
            acc.legs += shares.legs;
            acc.count++;
          }
        } catch {
          // Body-frame extraction can fail on degenerate poses; not fatal.
        }
      }
      const sample = {
        t,
        cda: a.cda,
        drag_power_w: a.drag_power_w,
        position: pipeline.position?.class,
        power_w: sensors?.power_w,
        hr_bpm: sensors?.heart_rate_bpm,
        cadence_rpm: sensors?.cadence_rpm,
        region_visibility: vis ?? undefined,
      };
      timeSeriesRef.current.push(sample);
      if (timeSeriesRef.current.length > 5000) {
        timeSeriesRef.current.shift();
      }
    }
  }, [pipeline.smoothed, ble, pipeline.position]);

  const a = pipeline.smoothed;
  const cal = pipeline.calibration;
  const pos = pipeline.position;
  const base = pipeline.baseline;

  // Wave 20.16 fix — Extract the End-Session action so both the internal
  // fullscreen button AND the parent's outer End button (via
  // endSessionRef) call the same persist-and-report flow. Building the
  // summary reads pipeline / ref state at call time.
  const handleEndSession = useCallback(() => {
    if (reportSummary) return; // already ended
    // Wave 20.49l — TAIL TRIM. When the rider reaches for the mouse
    // to click End (or hits E, or is about to), those last few
    // frames dip into a lean-forward that skews bestCda / the last
    // hold's cda_best downward. Cut the final SESSION_TAIL_TRIM_SEC
    // seconds off every accumulator before we build the summary.
    const SESSION_TAIL_TRIM_SEC = 3;
    const startAtWall = sessionStartedAtRef.current ?? performance.now();
    const nowWall = performance.now();
    const nowSec = nowWall / 1000;
    const trimmedNowSec = nowSec - SESSION_TAIL_TRIM_SEC;
    const trimmedT = Math.max(
      0,
      Math.round((nowWall - startAtWall) / 1000) - SESSION_TAIL_TRIM_SEC,
    );

    // finalizePositionSession now honors an explicit end cutoff so
    // the in-progress hold's duration doesn't include the trim
    // window, and the segmenter's `finish()` runs its bucket math
    // against the trimmed cut.
    const freshHolds = pipeline.finalizePositionSession(trimmedNowSec);
    const bestHold =
      freshHolds.length > 0
        ? freshHolds.reduce((best, h) => (h.cdaBest < best.cdaBest ? h : best), freshHolds[0])
        : null;

    // Trim the time-series (samples' `t` is seconds since session
    // start) and re-derive bestCda from the trimmed window so the
    // headline number doesn't reflect the lean-forward dip.
    const trimmedSeries = timeSeriesRef.current.filter((s) => s.t <= trimmedT);
    const trimmedBestCda = trimmedSeries.reduce<number | null>(
      (best, s) =>
        s.cda != null && Number.isFinite(s.cda) && s.cda > 0
          ? best == null || s.cda < best
            ? s.cda
            : best
          : best,
      null,
    );
    const bestCdaForSummary = trimmedBestCda ?? base?.bestCda;

    const summary = {
      baseline_cda: base?.cda,
      best_cda: bestCdaForSummary,
      bestCda: bestCdaForSummary,
      // Derive bestPosition from the winning hold so the summary
      // row + downstream analytics know which position produced the
      // best CdA. Before this fix bestPosition was always null.
      bestPosition: bestHold?.positionClass ?? undefined,
      powerCdaBest: pipeline.smoothed?.power_cda ?? undefined,
      source: isRaphaDomain() ? 'rapha' : 'public',
      focusClass: props.focusClass ?? undefined,
      startedAt: new Date(
        sessionStartedAtRef.current
          ? Date.now() - (performance.now() - sessionStartedAtRef.current)
          : Date.now(),
      ).toISOString(),
      endedAt: new Date().toISOString(),
      yaw_cda_at_10deg: yawCdaAt10,
      positions: freshHolds.map((h) => ({
        position: h.positionClass,
        cda_best: h.cdaBest,
        cda_mean: h.cdaMean,
        held_sec: h.durationSec,
      })),
      segments: pipeline.smoothed?.segments,
      power_cda: pipeline.smoothed?.power_cda,
      sensors: ble.getSensorData?.(),
      context: getAeroAiContext(),
      timeSeries: trimmedSeries.slice(),
      bodyHullStats: bodyHullStatsRef.current ?? undefined,
      regionVisibilityHighlights:
        visibilityAccRef.current.count > 0
          ? {
              head_visible_pct:
                visibilityAccRef.current.head / visibilityAccRef.current.count,
              torso_visible_pct:
                visibilityAccRef.current.torso / visibilityAccRef.current.count,
              arms_visible_pct:
                visibilityAccRef.current.arms / visibilityAccRef.current.count,
              legs_visible_pct:
                visibilityAccRef.current.legs / visibilityAccRef.current.count,
            }
          : undefined,
      pressureWeightedShares:
        pressureSharesAccRef.current.count > 0
          ? {
              head: pressureSharesAccRef.current.head / pressureSharesAccRef.current.count,
              torso: pressureSharesAccRef.current.torso / pressureSharesAccRef.current.count,
              arms: pressureSharesAccRef.current.arms / pressureSharesAccRef.current.count,
              legs: pressureSharesAccRef.current.legs / pressureSharesAccRef.current.count,
            }
          : undefined,
      // Wave 20.49q — per-session calibration snapshot handed off
      // from Preflight via localStorage (the camera lifecycle
      // between Preflight and AI can't share a ref). Server drops
      // the field when it can't parse the JSON or the payload
      // exceeds 40 KB, so we always attempt the attach.
      calibrationSnapshot: (() => {
        try {
          const raw = localStorage.getItem('aerochat.calibrationSnapshot');
          if (!raw) return undefined;
          const parsed = JSON.parse(raw);
          if (
            parsed &&
            parsed.shoulderMid &&
            parsed.hipMid &&
            typeof parsed.capturedAt === 'string'
          ) {
            return parsed;
          }
        } catch { /* malformed — skip */ }
        return undefined;
      })(),
    };
    setReportSummary(summary);
    const apiBase = process.env.REACT_APP_API || '';
    const token =
      localStorage.getItem(process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token') || '';
    fetch(`${apiBase}/sessions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(summary),
    })
      .then(async (r) => {
        // Wave 20.49q — clear the Preflight snapshot regardless of
        // response status. On success it's already persisted;
        // on failure a stale payload shouldn't leak into the
        // NEXT session's summary.
        try {
          localStorage.removeItem('aerochat.calibrationSnapshot');
        } catch { /* private mode — nothing to clean */ }
        // Wave 20.35 — actually check the response. Previously we
        // called r.json() unconditionally; on 400/401/403/500 the
        // body is a server error object like
        // { statusCode: 403, code: 'liability_acceptance_required', … }
        // which happily parsed as JSON and then got dumped into
        // SessionReport as raw text.
        if (!r.ok) {
          let msg = `Save failed (${r.status})`;
          let code: string | null = null;
          try {
            const body = await r.json();
            if (body?.message) msg = String(body.message);
            if (body?.code) code = String(body.code);
          } catch { /* body wasn't JSON */ }
          setSaveError({ message: msg, code, status: r.status });
          setReportSessionId(null);
          return null;
        }
        return r.json();
      })
      .then((s) => {
        if (s?.id) {
          setReportSessionId(s.id);
          setSaveError(null);
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('record session failed', err);
        setSaveError({
          message: err?.message || 'Network error saving session',
          code: null,
          status: 0,
        });
      });
  }, [
    reportSummary,
    base?.cda,
    base?.bestCda,
    pipeline.smoothed?.power_cda,
    pipeline.smoothed?.segments,
    pipeline.positionHolds,
    yawCdaAt10,
    ble,
    getAeroAiContext,
    props.focusClass,
  ]);

  // Expose the handler to the parent so an outer End button can trigger
  // it instead of merely exiting the session without persisting.
  useEffect(() => {
    if (!props.endSessionRef) return;
    props.endSessionRef.current = handleEndSession;
    return () => {
      if (props.endSessionRef) props.endSessionRef.current = null;
    };
  }, [handleEndSession, props.endSessionRef]);

  // Wave 20.49l — keep the 'E' hotkey pointing at the latest
  // handleEndSession closure (ref-latest pattern, avoids TDZ).
  useEffect(() => {
    endHandlerRef.current = handleEndSession;
    return () => {
      endHandlerRef.current = null;
    };
  }, [handleEndSession]);

  // ================================================================
  // Wave 20.49v — On-video virtual END SESSION button.
  //
  // Rider asked: same circle-hover-to-fire pattern the Preflight
  // Start button uses, but for ending the session. Solves the same
  // problem (reaching for the mouse knocks the rider out of aero
  // position and skews the final CdA readings).
  //
  // Placement: top-right corner (VBTN_END_CX, VBTN_END_CY) with a
  // smaller radius than Preflight (VBTN_END_R = 0.075). The rider
  // has to LIFT a hand off the bars into the top of the frame,
  // which is a deliberate gesture — a normal hands-on-hoods pose
  // won't accidentally trip it.
  //
  // Runs a lightweight HandDetector loop at ~5 Hz in parallel
  // with the vision pipeline; overhead is under 1 ms/tick.
  // ================================================================
  // Wave 20.49bm — The dedicated 5 Hz HandDetector + inline
  // circle hit-test is replaced by the shared GestureActionTray.
  // The tray subscribes to the pose pipeline's hand landmarks and
  // renders any actions we hand it along the bottom edge of the
  // video. We keep a lightweight detector loop here to feed the
  // tray with hand landmarks (the pipeline doesn't expose them
  // separately today — a follow-up will merge the two loops).
  const latestHandsRef = useRef<DetectedHand[]>([]);
  const sessionEnded = !!reportSummary;
  useEffect(() => {
    if (!cameraReady || sessionEnded) return;
    let cancelled = false;
    let timer: number | null = null;
    const detector = new HandDetector();
    let ready = false;
    detector.init().then(() => { ready = true; }).catch(() => {});
    const tick = () => {
      if (cancelled) return;
      const v = videoRef.current;
      if (v && v.readyState >= 2 && ready) {
        try {
          const r = detector.detect(v, performance.now());
          latestHandsRef.current = r?.hands ?? [];
        } catch { /* transient detector hiccup */ }
      }
      timer = window.setTimeout(tick, 200);
    };
    tick();
    return () => {
      cancelled = true;
      if (timer != null) window.clearTimeout(timer);
      try { detector.close(); } catch { /* already closed */ }
    };
  }, [cameraReady, sessionEnded]);
  // Wave 6.5 — corrected CdA for display. The raw pipeline value is what
  // the session reports + best tracker still use (so the server learns
  // from uncorrected ratios); the rider sees the corrected number.
  const displayedCda = a ? a.cda * visionCorrection : null;
  const correctionApplied = Math.abs(visionCorrection - 1.0) > 0.005;
  const showDragLine = props.dashboards.includes(Boards.dragLine);

  // Watts saved vs baseline at current velocity (40 km/h default)
  // ΔP = 0.5 × ρ × ΔCdA × v³
  const wattsVsBaseline =
    base.delta !== null ? -0.5 * 1.225 * base.delta * Math.pow(11.11, 3) : null;

  // Calibration status tone
  const calTone: 'good' | 'ok' | 'poor' =
    !cal || cal.confidence < 0.2
      ? 'poor'
      : cal.confidence < 0.6
        ? 'ok'
        : 'good';
  const calToneColor =
    calTone === 'good' ? colors.brand : calTone === 'ok' ? colors.warning : colors.danger;

  return (
    <ThemeProvider theme={props.theme}>
      {/* Wave 20.44 — Focus banner. Opens the session with a framing
          message tied to the position the rider chose on /session/focus.
          Auto-dismisses after 12 s, or immediately on the close button.
          Fixed-position so it floats above both the compact grid and
          fullscreen overlay. */}
      {props.focusClass && !focusBannerDismissed && (
        <Box
          sx={{
            position: 'fixed',
            top: { xs: 76, md: 92 },
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10000,
            maxWidth: 'calc(100vw - 32px)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 2,
            py: 1.25,
            borderRadius: 999,
            backgroundColor: 'rgba(10, 14, 26, 0.9)',
            backdropFilter: 'blur(16px)',
            border: `1px solid ${colors.borderFocus}`,
            boxShadow: `0 12px 32px rgba(0, 0, 0, 0.5)`,
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: colors.brand,
              boxShadow: `0 0 8px ${colors.brand}`,
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              color: colors.text,
              fontSize: '0.85rem',
              fontWeight: 600,
              lineHeight: 1.35,
              minWidth: 0,
            }}
          >
            {props.focusClass === 'new' || !focusPriorBest ? (
              <>
                <Box component="span" sx={{ color: colors.brand, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', mr: 0.75 }}>
                  New position
                </Box>
                — let's record it and work on reducing your drag.
              </>
            ) : (
              <>
                <Box component="span" sx={{ color: colors.brand, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', mr: 0.75 }}>
                  {labelForPosition(props.focusClass as any)}
                </Box>
                — best so far{' '}
                <Box component="span" sx={{ fontFamily: '"JetBrains Mono", monospace', color: colors.brand, fontWeight: 700 }}>
                  {focusPriorBest.bestCda.toFixed(3)}
                </Box>
                . Let's make some improvements.
              </>
            )}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setFocusBannerDismissed(true)}
            aria-label="Dismiss focus banner"
            sx={{ color: colors.textMuted, ml: 0.5, '&:hover': { color: colors.text } }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      )}
      <Box
        sx={
          fullscreen
            ? {
                // Fullscreen: fixed overlay covering the entire viewport.
                // Video fills the screen; sidebar floats on top.
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                backgroundColor: '#000',
              }
            : {
                // Compact: side-by-side grid with video + sidebar.
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  lg: 'minmax(0, 1fr) 400px',
                },
                alignItems: 'start',
                gap: 3,
                p: { xs: 2, md: 3 },
              }
        }
      >
        {/* Video + overlays. Sticky + aspect-ratio in compact; absolute fill
            in fullscreen. */}
        <Box
          sx={
            fullscreen
              ? {
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: '#000',
                }
              : {
                  position: { xs: 'relative', lg: 'sticky' },
                  top: { lg: 92 },
                  borderRadius: 3,
                  overflow: 'hidden',
                  backgroundColor: '#000',
                  border: `1px solid ${colors.border}`,
                  aspectRatio: '16 / 9',
                  width: '100%',
                  maxHeight: { lg: 'calc(100vh - 108px)' },
                }
          }
        >
          {/* Inner wrapper for the video + all overlay canvases. This is the
              element that rotates when the user clicks the rotate button.
              MediaPipe reads raw frames from the video element regardless of
              CSS rotation, so detection is unaffected. When rotated 90/270,
              the wrapper swaps width/height so it fills the container. */}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              transform: vizModes.rotation ? `rotate(${vizModes.rotation}deg)` : undefined,
              transformOrigin: 'center center',
              // When rotated 90/270, swap the width and height so the
              // rotated frame fills the container.
              ...(vizModes.rotation === 90 || vizModes.rotation === 270
                ? { width: '100%', height: '100%' }
                : {}),
              transition: 'transform 300ms ease',
            }}
          >
            <video
              ref={videoRef}
              data-onboard="camera-framing"
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'fill',
                visibility: vizModes.videoOff ? 'hidden' : 'visible',
              }}
            />
            <VisionOverlay
              videoRef={videoRef}
              refs={pipeline.visualization}
              modes={vizModes}
              regionWatts={a?.segments ? {
                head: a.segments.head_w,
                torso: a.segments.torso_w,
                arms: a.segments.arms_w,
                legs: a.segments.legs_w,
              } : undefined}
            />
            {/* Wave 20.44 — Rider report: the 3D avatar / digital
                twin overlays (AvatarOverlay + PressureMesh) don't
                land visually — cylinder mesh floats off-body, avatar
                clips into the rider. Hiding both until we can either
                fix the alignment or ship a next-gen fitter. All
                underlying calc (smplFit, keyframe pipeline, region
                shares) still runs so the aero math is unaffected —
                only the on-video visualization is gone. Re-enable
                by uncommenting when accuracy is dialed. */}
            <MeshCanvas
              videoRef={videoRef}
              refs={pipeline.visualization}
              visible={vizModes.mesh && cameraReady}
            />

            {/* Wave 20.49cu — GestureActionBar: a full-width strip
                across the top of the video that OWNS that space.
                Big pill buttons; the Live pill + Fullscreen toggle
                live in its right slot so nothing overlaps. Takes
                the hand-detector REF (read per frame) and the
                video element (cover-crop math). Fires via hover-
                dwell (1.2 s for the destructive action), thumbs-up,
                or click. */}
            {cameraReady && (
              <GestureActionBar
                mirrorX={false}
                handsRef={latestHandsRef}
                videoRef={videoRef}
                dwellMs={1200}
                actions={
                  sessionEnded
                    ? []
                    : [
                        {
                          id: 'end-session',
                          label: 'End Session',
                          icon: <StopCircleIcon />,
                          tone: 'danger',
                          onFire: () => {
                            const fn = endHandlerRef.current;
                            if (fn) fn();
                          },
                        },
                      ]
                }
                onArm={(id) => {
                  if (id === 'end-session') {
                    outerVoiceRef.current?.speak({
                      text: 'Ready to end session — hold, or thumbs up.',
                      category: 'interrupt',
                    });
                  }
                }}
                trailing={
                  <>
                    {/* Live pill */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 999,
                        backgroundColor: 'rgba(10, 14, 26, 0.7)',
                        backdropFilter: 'blur(12px)',
                        border: `1px solid ${colors.border}`,
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: colors.brand,
                          boxShadow: `0 0 10px ${colors.brand}`,
                          animation: 'pulse 2s infinite',
                          '@keyframes pulse': {
                            '0%, 100%': { opacity: 1 },
                            '50%': { opacity: 0.4 },
                          },
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: '0.6875rem',
                          fontWeight: 600,
                          letterSpacing: '0.12em',
                          textTransform: 'uppercase',
                          color: colors.text,
                        }}
                      >
                        {t('live', { fps: pipeline.fps })}
                      </Typography>
                    </Box>
                    {/* Fullscreen toggle */}
                    <Tooltip title={fullscreen ? t('fullscreen.exitEsc') : t('fullscreen.enter')}>
                      <IconButton
                        onClick={() => setFullscreen(!fullscreen)}
                        sx={{
                          backgroundColor: 'rgba(10, 14, 26, 0.7)',
                          backdropFilter: 'blur(12px)',
                          border: `1px solid ${colors.border}`,
                          color: colors.text,
                          width: 36,
                          height: 36,
                          '&:hover': {
                            backgroundColor: 'rgba(10, 14, 26, 0.9)',
                            borderColor: colors.brand,
                            color: colors.brand,
                          },
                        }}
                      >
                        {fullscreen ? <FullscreenExitIcon sx={{ fontSize: 20 }} /> : <FullscreenIcon sx={{ fontSize: 20 }} />}
                      </IconButton>
                    </Tooltip>
                  </>
                }
              />
            )}
          </Box>
          {cameraReady && (
            <VisualizationToolbar modes={vizModes} onChange={updateVizModes} />
          )}

          {!cameraReady && !cameraError && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                gap: 2,
                backgroundColor: 'rgba(10, 14, 26, 0.9)',
              }}
            >
              <CircularProgress sx={{ color: colors.brand }} />
              <Typography variant="body2" sx={{ color: colors.textMuted }}>
                {t('camera.waiting')}
              </Typography>
            </Box>
          )}

          {/* Wave 20.49cu — the Live pill + Fullscreen toggle moved
              into GestureActionBar's right slot (rendered inside the
              video box above), so the top strip has one owner. */}

          {/* Fullscreen floating action bar — only shown in fullscreen mode. */}
          {fullscreen && cameraReady && (
            <Stack
              direction="row"
              spacing={1.5}
              sx={{
                position: 'absolute',
                bottom: 24,
                right: 24,
                zIndex: 20,
              }}
            >
              <Button
                onClick={() => setFullscreen(false)}
                startIcon={<FullscreenExitIcon />}
                sx={{
                  px: 2.5,
                  py: 1.25,
                  backgroundColor: 'rgba(10, 14, 26, 0.78)',
                  backdropFilter: 'blur(16px)',
                  border: `1px solid ${colors.border}`,
                  color: colors.text,
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: 'rgba(18, 24, 38, 0.9)',
                    borderColor: colors.brand,
                  },
                }}
              >
                {t('fullscreen.exit')}
              </Button>
              {props.onEndSession && (
                <Button
                  onClick={handleEndSession}
                  startIcon={<StopIcon />}
                  sx={{
                    px: 2.5,
                    py: 1.25,
                    backgroundColor: 'rgba(255, 71, 87, 0.12)',
                    backdropFilter: 'blur(16px)',
                    border: `1px solid rgba(255, 71, 87, 0.4)`,
                    color: colors.danger,
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: 'rgba(255, 71, 87, 0.22)',
                      borderColor: colors.danger,
                    },
                  }}
                >
                  {t('endSession')}
                </Button>
              )}
            </Stack>
          )}
        </Box>

        {/* Stats sidebar. In compact mode this is the second grid cell; in
            fullscreen it becomes a floating glass panel on the right that
            overlays the video. */}
        <Stack
          spacing={2.5}
          sx={
            fullscreen
              ? {
                  position: 'absolute',
                  top: 80,
                  right: 24,
                  bottom: 96, // leave room for the bottom action bar
                  width: 380,
                  maxWidth: 'calc(100vw - 48px)',
                  p: 2,
                  borderRadius: 3,
                  backgroundColor: 'rgba(10, 14, 26, 0.72)',
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  border: `1px solid ${colors.border}`,
                  boxShadow: '0 24px 48px rgba(0, 0, 0, 0.55)',
                  overflowY: 'auto',
                  zIndex: 15,
                }
              : undefined
          }
        >
          {cameraError && <Alert severity="error">{cameraError}</Alert>}
          {pipeline.error && <Alert severity="error">{pipeline.error}</Alert>}

          {/* Bluetooth sensors */}
          <BluetoothPanel ble={ble} />

          {/* Wave 20.44 — ERG target chip. Only surfaces when the
              paired trainer supports FTMS. Preflight already sent
              the target on pairing; here the rider can nudge it
              mid-session without breaking form. Reads actual power
              from the pipeline so they can see how close they are
              to the target. */}
          {ble.hasErgControl && (
            <Box
              sx={{
                p: 1.75,
                borderRadius: 2,
                border: `1px solid ${colors.borderFocus}`,
                backgroundColor: 'rgba(106,255,69,0.06)',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <BoltIcon sx={{ color: colors.brand, fontSize: 18 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: '0.625rem',
                      fontWeight: 700,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: colors.brand,
                    }}
                  >
                    ERG TARGET
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontWeight: 800,
                      color: colors.brand,
                      fontSize: '1.35rem',
                      lineHeight: 1,
                      mt: 0.5,
                    }}
                  >
                    {ble.targetPowerW ?? 200}
                    <Box component="span" sx={{ fontSize: '0.75rem', ml: 0.5, color: colors.textMuted }}>
                      W
                    </Box>
                    {ble.power?.watts != null && (
                      <Box
                        component="span"
                        sx={{
                          fontSize: '0.75rem',
                          ml: 1.5,
                          color: colors.textMuted,
                          fontFamily: '"JetBrains Mono", monospace',
                          fontWeight: 600,
                        }}
                      >
                        actual {Math.round(ble.power.watts)} W
                      </Box>
                    )}
                  </Typography>
                </Box>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Button
                    size="small"
                    onClick={() => ble.setTargetPower((ble.targetPowerW ?? 200) - 5)}
                    sx={{
                      minWidth: 32,
                      color: colors.text,
                      border: `1px solid ${colors.border}`,
                    }}
                  >
                    −5
                  </Button>
                  <Button
                    size="small"
                    onClick={() => ble.setTargetPower((ble.targetPowerW ?? 200) + 5)}
                    sx={{
                      minWidth: 32,
                      color: colors.text,
                      border: `1px solid ${colors.border}`,
                    }}
                  >
                    +5
                  </Button>
                </Stack>
              </Stack>
            </Box>
          )}

          {/* Calibration + Position status strip */}
          <Box
            data-onboard="calibration-status"
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: colors.surface,
              border: `1px solid ${colors.border}`,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: calToneColor,
                    boxShadow: `0 0 8px ${calToneColor}`,
                  }}
                />
                <Tooltip
                  title={
                    cal
                      ? t('calibration.confidence', { pct: (cal.confidence * 100).toFixed(0) })
                      : t('calibration.waiting')
                  }
                >
                  <Typography
                    sx={{
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: colors.textMuted,
                    }}
                  >
                    {t('calibration.calibratedVia')}{' '}
                    <Box component="span" sx={{ color: calToneColor }}>
                      {cal ? t(`calibration.${cal.source}`) : '—'}
                    </Box>
                  </Typography>
                </Tooltip>
                {/* Wave 13 / Track E — surfaces the calibration-lock state.
                    Once the rider's body dims have been stable for ~2 s, the
                    pipeline freezes px_per_m + bone_offsets so off-axis drift
                    can't slowly bias the CdA. */}
                {cal?.calibration_locked && (
                  <Box
                    sx={{
                      ml: 1,
                      px: 0.875,
                      py: 0.125,
                      borderRadius: 999,
                      backgroundColor: 'rgba(106, 255, 69, 0.12)',
                      border: `1px solid ${colors.borderFocus}`,
                      fontSize: '0.625rem',
                      letterSpacing: '0.08em',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      color: colors.brand,
                    }}
                  >
                    {t('calibration.locked')}
                  </Box>
                )}
                {/* Wave 20.35 — "reposition camera" warning is now
                    dismissible. Riders with intentionally unusual
                    setups (wide-angle phone rotated vertical, etc.)
                    should be able to acknowledge the warning and
                    proceed. The underlying gate change (OR not AND)
                    means the pipeline runs regardless — the banner
                    was purely advisory anyway. */}
                {(pipeline.calibrationUnusableForMs ?? 0) > 15000 && !repositionDismissed && (
                  <Box
                    sx={{
                      ml: 1,
                      px: 0.875,
                      py: 0.125,
                      borderRadius: 999,
                      backgroundColor: 'rgba(255, 181, 71, 0.14)',
                      border: `1px solid rgba(255, 181, 71, 0.6)`,
                      fontSize: '0.625rem',
                      letterSpacing: '0.08em',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      color: colors.warning,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 0.75,
                    }}
                  >
                    {t('calibration.repositionCamera')}
                    <Box
                      component="button"
                      onClick={() => setRepositionDismissed(true)}
                      sx={{
                        border: 'none',
                        background: 'transparent',
                        color: colors.warning,
                        opacity: 0.85,
                        cursor: 'pointer',
                        fontWeight: 700,
                        fontSize: '0.625rem',
                        letterSpacing: '0.05em',
                        p: 0,
                        textDecoration: 'underline',
                        '&:hover': { opacity: 1 },
                      }}
                    >
                      Camera's fine
                    </Box>
                  </Box>
                )}
              </Box>
              {pos && pos.class !== 'unknown' && (
                <Box
                  sx={{
                    px: 1.25,
                    py: 0.25,
                    borderRadius: 999,
                    backgroundColor: 'rgba(0, 217, 255, 0.08)',
                    border: `1px solid rgba(0, 217, 255, 0.3)`,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.625rem',
                      fontWeight: 700,
                      letterSpacing: '0.1em',
                      textTransform: 'uppercase',
                      color: colors.data,
                    }}
                  >
                    {labelForPosition(pos.class)}
                  </Typography>
                </Box>
              )}
            </Stack>
          </Box>

          {/* Wave 20.44 — Session guide. Rider always sees the
              current state + a clear next step. Baseline gets a
              progress bar and a stall hint after 45 s; position
              hold gets its own progress toward the 15-s recording
              threshold. */}
          <SessionGuide
            baselineLocked={base.isLocked}
            baselineProgress={base.lockProgress}
            positionLabel={pos && pos.class !== 'unknown' ? labelForPosition(pos.class) : null}
            heldCount={pipeline.positionHolds.length}
            lastHold={pipeline.positionHolds[pipeline.positionHolds.length - 1] ?? null}
            currentHold={pipeline.currentHold}
            segments={a?.segments ?? null}
          />
          <SessionNarrator
            baselineLocked={base.isLocked}
            positionLabel={pos && pos.class !== 'unknown' ? labelForPosition(pos.class) : null}
            heldCount={pipeline.positionHolds.length}
            lastHold={pipeline.positionHolds[pipeline.positionHolds.length - 1] ?? null}
            currentHold={pipeline.currentHold}
            topDragTip={topDragTip(a?.segments ?? null)}
          />

          {/* Primary CdA + delta tile */}
          <Box
            data-onboard="cda-tile"
            sx={{
              p: 3,
              borderRadius: 3,
              border: `1px solid ${colors.borderFocus}`,
              backgroundColor: 'rgba(106, 255, 69, 0.04)',
              boxShadow: `0 0 0 1px ${colors.brandShadow}, 0 12px 32px rgba(106, 255, 69, 0.08)`,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.6875rem',
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: colors.brand,
                mb: 1.5,
              }}
            >
              {t('cda.label')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '3.5rem',
                  fontWeight: 700,
                  color: colors.brand,
                  lineHeight: 1,
                  letterSpacing: '-0.03em',
                  textShadow: `0 0 32px ${colors.brandShadow}`,
                }}
              >
                {displayedCda != null ? displayedCda.toFixed(3) : '—'}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: colors.textMuted,
                  fontFamily: '"JetBrains Mono", monospace',
                }}
              >
                m²
              </Typography>
              {/* Wave 13 / Track D — label which correction is active. The
                  "personal" tag means the rider has paired enough power
                  sessions for a learned correction; "population" means the
                  cohort prior is seeding their reading until they pair. */}
              {correctionApplied && (
                <Box
                  sx={{
                    ml: 1,
                    px: 0.875,
                    py: 0.125,
                    borderRadius: 999,
                    backgroundColor:
                      visionCorrectionSource === 'personal'
                        ? 'rgba(106, 255, 69, 0.12)'
                        : 'rgba(0, 217, 255, 0.10)',
                    border: `1px solid ${
                      visionCorrectionSource === 'personal'
                        ? colors.borderFocus
                        : 'rgba(0, 217, 255, 0.35)'
                    }`,
                    fontSize: '0.625rem',
                    letterSpacing: '0.08em',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    color:
                      visionCorrectionSource === 'personal' ? colors.brand : colors.data,
                  }}
                >
                  {t(`cda.correction.${visionCorrectionSource}`)}
                </Box>
              )}
            </Box>

            {/* Baseline state: either "locking in..." progress or delta display */}
            {!base.isLocked && a && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  sx={{
                    fontSize: '0.6875rem',
                    fontWeight: 600,
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                    color: colors.textMuted,
                    mb: 0.75,
                  }}
                >
                  {t('cda.lockingBaseline', { pct: (base.lockProgress * 100).toFixed(0) })}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={base.lockProgress * 100}
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: colors.surfaceElevated,
                    '& .MuiLinearProgress-bar': {
                      background: colors.gradBrand,
                    },
                  }}
                />
              </Box>
            )}
            {base.isLocked && base.delta !== null && a && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'baseline', gap: 1.5, flexWrap: 'wrap' }}>
                <Typography
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: base.delta < 0 ? colors.brand : colors.danger,
                  }}
                >
                  {base.delta < 0 ? '↓' : '↑'} {Math.abs(base.delta).toFixed(3)} m²
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: colors.textMuted,
                    fontFamily: '"JetBrains Mono", monospace',
                  }}
                >
                  {t('cda.vsBaseline', { cda: base.cda?.toFixed(3) })}
                </Typography>
                {/* Wave 20.49l — mid-session recapture. When the
                    first baseline was captured badly the rider had
                    no way to fix it without ending + restarting. */}
                <Typography
                  role="button"
                  tabIndex={0}
                  onClick={() => setRecaptureOpen(true)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setRecaptureOpen(true);
                    }
                  }}
                  sx={{
                    ml: 'auto',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: colors.textMuted,
                    cursor: 'pointer',
                    borderBottom: `1px dashed ${colors.borderStrong}`,
                    '&:hover': { color: colors.brand, borderColor: colors.brand },
                  }}
                >
                  Recapture baseline
                </Typography>
              </Box>
            )}
            {pipeline.isRefining && (
              <Typography
                sx={{
                  mt: 1.5,
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: colors.data,
                }}
              >
                {t('cda.refining')}
              </Typography>
            )}
          </Box>

          {/* Wave 20.44 — Silhouette body + face mesh hidden with
              the rest of the digital-twin family until we can dial
              in the fit. Backing pipeline (headHullMesh, bodyHullMesh,
              faceLandmarks) still runs. */}

          {/* Secondary stats grid */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 2,
            }}
          >
            <StatCard
              label={t('stats.dragPower')}
              value={a ? a.drag_power_w.toFixed(0) : '—'}
              unit="W"
              mini
            />
            <StatCard
              label={t('stats.frontalArea')}
              value={a ? a.frontal_area_m2.toFixed(3) : '—'}
              unit="m²"
              mini
            />
            <StatCard
              label={t('stats.vsBaseline')}
              value={wattsVsBaseline !== null ? (wattsVsBaseline >= 0 ? '+' : '') + wattsVsBaseline.toFixed(0) : '—'}
              unit="W"
              tone={wattsVsBaseline !== null && wattsVsBaseline > 0 ? 'brand' : 'default'}
              mini
            />
            <StatCard
              label={t('stats.bestCda')}
              value={base.bestCda ? base.bestCda.toFixed(3) : '—'}
              unit="m²"
              tone="data"
              mini
            />
            {ble.powerConnected && (
              <StatCard
                label={t('stats.power')}
                value={ble.power ? ble.power.watts.toString() : '—'}
                unit="W"
                tone="brand"
                mini
              />
            )}
            {ble.powerConnected && ble.power?.cadenceRpm !== null && (
              <StatCard
                label={t('stats.cadence')}
                value={ble.power?.cadenceRpm?.toString() ?? '—'}
                unit="rpm"
                mini
              />
            )}
            {ble.hrConnected && (
              <StatCard
                label={t('stats.heartRate')}
                value={ble.heartRate ? ble.heartRate.bpm.toString() : '—'}
                unit="bpm"
                mini
              />
            )}
            {a?.predicted_speed_kph !== undefined && (
              <StatCard
                label={t('stats.predicted')}
                /* Wave 20.70 — was hardcoded km/h on the most-used
                   live screen in the app. This file had no unit
                   awareness at all. */
                value={fmtSpeed(a.predicted_speed_kph / 3.6, units).split(' ')[0]}
                unit={fmtSpeed(a.predicted_speed_kph / 3.6, units).split(' ')[1] ?? ''}
                tone="data"
                mini
              />
            )}
            {a?.power_cda != null && (
              <StatCard
                label={t('stats.powerCda')}
                value={a.power_cda.toFixed(3)}
                unit="m²"
                tone={
                  a.power_vision_agreement != null && a.power_vision_agreement >= 0.5
                    ? 'brand'
                    : 'default'
                }
                mini
              />
            )}
          </Box>

          {/* Track 1.1 — Power vs Vision CdA agreement pill.
              Surfaces credibility when a power meter is connected:
              green = the camera and your power meter agree,
              amber = drift, red = calibration warning. */}
          {a?.power_cda != null && a?.power_vision_agreement != null && (
            <Box
              sx={{
                px: 2,
                py: 1,
                borderRadius: 2,
                backgroundColor: colors.surface,
                border: `1px solid ${
                  a.power_vision_agreement >= 0.5
                    ? colors.brand
                    : a.power_vision_agreement >= 0.2
                      ? colors.warning
                      : colors.danger
                }`,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor:
                    a.power_vision_agreement >= 0.5
                      ? colors.brand
                      : a.power_vision_agreement >= 0.2
                        ? colors.warning
                        : colors.danger,
                  boxShadow: `0 0 8px ${
                    a.power_vision_agreement >= 0.5
                      ? colors.brand
                      : a.power_vision_agreement >= 0.2
                        ? colors.warning
                        : colors.danger
                  }`,
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: colors.text,
                  flexGrow: 1,
                }}
              >
                {a.power_vision_agreement >= 0.5
                  ? t('stats.powerAgreementOk')
                  : a.power_vision_agreement >= 0.2
                    ? t('stats.powerAgreementDrift')
                    : t('stats.powerAgreementWarn')}
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.75rem',
                  color: colors.textMuted,
                }}
              >
                {(a.power_vision_agreement * 100).toFixed(0)}%
              </Typography>
            </Box>
          )}

          {/* Body segment breakdown */}
          {a?.segments && (
            <Box
              sx={{
                p: 3,
                borderRadius: 3,
                backgroundColor: colors.surface,
                border: `1px solid ${colors.border}`,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: colors.textMuted,
                  mb: 2,
                }}
              >
                {t('segments.title')}
              </Typography>
              <Stack spacing={1.75}>
                {(['head', 'torso', 'arms', 'legs'] as const).map((seg) => {
                  const pct = a.segments![`${seg}_pct` as keyof typeof a.segments] as number;
                  return (
                    <Box key={seg}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: colors.text,
                            textTransform: 'capitalize',
                          }}
                        >
                          {t(`segments.${seg}`)}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: '"JetBrains Mono", monospace',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            color: colors.brand,
                          }}
                        >
                          {pct.toFixed(0)}%
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          height: 6,
                          borderRadius: 999,
                          backgroundColor: colors.bgSubtle,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          sx={{
                            height: '100%',
                            width: `${pct}%`,
                            background: colors.gradBrand,
                            borderRadius: 999,
                            transition: 'width 320ms',
                          }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}

          {/* Track 2.3 — side-by-side position comparison when two distinct
              holds have been observed. Stays mounted between detection
              events, hidden when no comparison is available. */}
          {pipeline.abComparison && (
            <PositionComparison ab={pipeline.abComparison} />
          )}

          {/* Wave 20.44 — Sidebar avatar panel hidden with the rest
              of the digital-twin family until we can dial in the
              fit. Underlying SMPL fitter still runs. */}

          {/* Track 5.1 — yaw sweep prompt. Mounts after baseline lock so
              the rider has settled before being asked to look 20° each way. */}
          <YawSweepPrompt
            phase={yawPhase}
            prompt={yawPrompt}
            onStart={() => yawSweepRef.current.start()}
            onSkip={() => setYawPhase('idle')}
            cdaAt10Deg={yawCdaAt10}
            baselineCda={base.cda}
          />

          {/* Aero AI observation feed — driven by the Track 3.1 coach bus
              plus the Track 4.4 keyframe vision analysis. */}
          <AeroAiPanel
            wsUrl={aeroAiWsUrl}
            userId="local-dev"
            sessionId="local-session"
            getContext={getAeroAiContext}
            /* Wave 20.35 — was `base.isLocked && cal.confidence > 0.3`.
               `base.isLocked` also requires the calibration to be
               stable, so during a marginal-angle session (see the
               Aug 6 empty-session bug) coach messages never emitted
               even when frames were being processed. Drop the lock
               requirement and lower the confidence floor so the
               rider gets *some* feedback even before the pipeline
               fully settles. */
            ready={!!cal && cal.confidence > 0.15}
            positionClass={pos?.class ?? null}
            coachBus={pipeline.coachBus}
            mode={isRaphaDomain() ? 'rapha_event' : 'default'}
            locale={i18n.language}
            videoRef={videoRef}
          />

          {/* Timeline chart */}
          {showDragLine && (
            <Box
              sx={{
                p: 3,
                borderRadius: 3,
                backgroundColor: colors.surface,
                border: `1px solid ${colors.border}`,
                position: 'relative',
                height: 180,
              }}
            >
              <Typography
                sx={{
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: colors.textMuted,
                  mb: 1,
                }}
              >
                {t('timeline.title')}
              </Typography>
              <Box sx={{ position: 'relative', height: 130 }}>
                <Stream data={streamDataRef.current} width={'100%'} />
              </Box>
            </Box>
          )}
        </Stack>
      </Box>
      {/* Wave 20.35 — save-error banner. Sits at the very top so a
          403 or 500 from the sessions POST is visible instead of the
          old behaviour where the error body was silently rendered
          inside SessionReport as raw JSON. */}
      {saveError && (
        <Box
          sx={{
            position: 'fixed',
            top: 12,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10_100,
            maxWidth: 520,
            width: 'calc(100vw - 32px)',
            px: 2,
            py: 1.25,
            borderRadius: 2,
            backgroundColor: '#3a1414',
            border: `1px solid #e05252`,
            boxShadow: '0 6px 22px rgba(0,0,0,0.4)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ color: '#ff9a9a', fontSize: '0.72rem', letterSpacing: '0.1em', fontWeight: 800 }}>
              SESSION SAVE FAILED
            </Typography>
            <Typography sx={{ color: colors.text, fontSize: '0.85rem', fontWeight: 600 }}>
              {saveError.message}
            </Typography>
            {saveError.code === 'liability_acceptance_required' && (
              <Button
                href="/liability"
                size="small"
                variant="outlined"
                sx={{ mt: 0.75, color: colors.brand, borderColor: colors.brand, fontWeight: 700 }}
              >
                Re-accept liability
              </Button>
            )}
          </Box>
          <Button
            onClick={() => setSaveError(null)}
            size="small"
            sx={{ color: colors.textMuted, minWidth: 0 }}
          >
            Dismiss
          </Button>
        </Box>
      )}

      {/* Wave 20.49l — Recapture baseline confirm. Wipes bestCda,
          baselineCda, all position holds, the CdA One-Euro filter,
          and the classifier hysteresis so the next 5 seconds of a
          steady upright hold re-lock a fresh baseline. */}
      {recaptureOpen && (
        <Box
          onClick={() => setRecaptureOpen(false)}
          sx={{
            position: 'fixed', inset: 0, zIndex: 10_050,
            backgroundColor: 'rgba(8,10,18,0.88)',
            backdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3,
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              maxWidth: 420, width: '100%',
              p: 3, borderRadius: 3,
              backgroundColor: colors.surfaceElevated ?? colors.surface,
              border: `1px solid ${colors.brand}`,
            }}
          >
            <Typography sx={{ color: colors.brand, fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.2em', mb: 1 }}>
              RECAPTURE BASELINE
            </Typography>
            <Typography sx={{ color: colors.text, fontSize: '1.05rem', fontWeight: 700, mb: 1 }}>
              Clear this session's baseline and lock a new one?
            </Typography>
            <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem', mb: 3 }}>
              This wipes your current baseline, best CdA, and all position holds for this session.
              You'll re-lock by holding upright with hands out for about five seconds.
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button onClick={() => setRecaptureOpen(false)} sx={{ color: colors.textMuted }}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  pipeline.resetBaseline();
                  // Voice singleton dedupes, so this is safe to
                  // call again in quick succession.
                  try {
                    outerVoiceRef.current?.speak({
                      text: 'Recapturing baseline — hold upright with hands out for five seconds.',
                      category: 'baseline',
                    });
                  } catch { /* voice may not be set up yet */ }
                  setRecaptureOpen(false);
                }}
                sx={{ backgroundColor: colors.brand, color: '#0A0E1A', fontWeight: 800 }}
              >
                Recapture
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      {/* Track 5.2 — session report overlay. Mounts on top of everything;
          closing it calls the parent's onEndSession so the rider actually
          leaves the session view. Wave 20.35 — hide when save failed;
          no point rendering an empty report with a broken sessionId. */}
      {reportSummary && !saveError && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 10_000,
            backgroundColor: 'rgba(8, 10, 18, 0.92)',
            backdropFilter: 'blur(12px)',
            overflowY: 'auto',
            p: { xs: 2, md: 6 },
          }}
        >
          <Box sx={{ maxWidth: 760, mx: 'auto' }}>
            <SessionReport
              summary={reportSummary}
              mode={isRaphaDomain() ? 'report_rapha' : 'report'}
              locale={i18n.language}
              aeroAiWsUrl={aeroAiWsUrl}
              apiBase={process.env.REACT_APP_API || ''}
              authToken={
                localStorage.getItem(
                  process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token',
                ) || ''
              }
              sessionId={reportSessionId}
            />
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Button
                onClick={() => {
                  setReportSummary(null);
                  setReportSessionId(null);
                  props.onEndSession?.();
                }}
                sx={{
                  px: 3,
                  py: 1.25,
                  backgroundColor: colors.brand,
                  color: colors.textInverse,
                  fontWeight: 700,
                  '&:hover': { backgroundColor: colors.brand, opacity: 0.9 },
                }}
              >
                {t('report.close')}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      {/* Wave 16 — usage HUD chip. Positioned as a floating pill so it
          works in both compact grid and fullscreen. Hidden entirely on
          rapha / role='rapha' by the component itself. */}
      <Box
        sx={{
          position: 'fixed',
          top: 12,
          right: 12,
          zIndex: 10000,
          pointerEvents: 'none',
        }}
      >
        <UsageChip />
      </Box>
      <PaywallModal
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        reason={paywallReason}
      />
      <OnboardingWalkthrough
        active={showOnboarding}
        onComplete={completeOnboarding}
        wsUrl={aeroAiWsUrl}
        userId={readCurrentUser().id?.toString() ?? undefined}
        locale={i18n.language}
      />
    </ThemeProvider>
  );
}

// -----------------------------------------------------------------
// Wave 20.44 — Session guide + narrator
// -----------------------------------------------------------------

const BASELINE_STALL_MS = 45_000;
const RECORDED_FLASH_MS = 5000;
const INTERRUPT_HINT_MS = 4000;
const INTERRUPT_THRESHOLD_SEC = 4;

interface SessionSegments {
  head_pct?: number;
  torso_pct?: number;
  arms_pct?: number;
  legs_pct?: number;
  head_w?: number;
  torso_w?: number;
  arms_w?: number;
  legs_w?: number;
}

/** Rule-based drag coach. Returns the top offender region + a
 *  concrete "try X" suggestion. Purely mechanical so it works
 *  even if the AI service is down and doesn't burn credits. */
function topDragTip(seg: SessionSegments | null): {
  region: 'head' | 'torso' | 'arms' | 'legs';
  pct: number;
  tip: string;
} | null {
  if (!seg) return null;
  // Wave 20.47e — vision service emits head_pct/torso_pct/arms_pct/
  // legs_pct as PERCENTAGES already (px / total * 100). The prior
  // × 100 here was a leftover assumption that treated them as
  // fractions — rider saw "legs 3000%" in the drag tip banner.
  const regions: Array<{ region: 'head' | 'torso' | 'arms' | 'legs'; pct: number; tip: string }> = [
    { region: 'head',  pct: seg.head_pct  ?? 0, tip: 'Lower your head or tuck your chin — pull your gaze in toward the stem.' },
    { region: 'torso', pct: seg.torso_pct ?? 0, tip: 'Flatten your back. Bend your elbows and drop your shoulders toward the bars.' },
    { region: 'arms',  pct: seg.arms_pct  ?? 0, tip: 'Tuck your elbows in tight to your body — narrow, not chicken-winged.' },
    { region: 'legs',  pct: seg.legs_pct  ?? 0, tip: 'Keep your knees close to the top tube. Bowed legs push out to the wind.' },
  ];
  regions.sort((a, b) => b.pct - a.pct);
  const top = regions[0];
  if (top.pct < 5) return null;
  return top;
}

function SessionGuide({
  baselineLocked,
  baselineProgress,
  positionLabel,
  heldCount,
  lastHold,
  currentHold,
  segments,
}: {
  baselineLocked: boolean;
  baselineProgress: number;
  positionLabel: string | null;
  heldCount: number;
  lastHold: { positionClass: string; cdaBest: number; durationSec: number } | null;
  currentHold: {
    positionClass: string;
    elapsedSec: number;
    minRequiredSec: number;
  } | null;
  segments: SessionSegments | null;
}) {
  const mountedAtRef = useRef<number>(Date.now());
  const [now, setNow] = useState<number>(Date.now());
  React.useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(id);
  }, []);
  const stalled = !baselineLocked && now - mountedAtRef.current > BASELINE_STALL_MS;

  // Celebrate the moment a hold is recorded.
  const prevHeldCountRef = useRef<number>(heldCount);
  const [recordedUntil, setRecordedUntil] = useState<number>(0);
  React.useEffect(() => {
    if (heldCount > prevHeldCountRef.current) {
      setRecordedUntil(Date.now() + RECORDED_FLASH_MS);
    }
    prevHeldCountRef.current = heldCount;
  }, [heldCount]);
  const recorded = now < recordedUntil;

  // Detect "you moved before it recorded" — the segmenter resets
  // its in-progress hold when the class flips. Flag it if elapsed
  // dropped from a meaningful count back near zero without
  // heldCount ticking up.
  const prevElapsedRef = useRef<number>(0);
  const [interruptedUntil, setInterruptedUntil] = useState<number>(0);
  React.useEffect(() => {
    const el = currentHold?.elapsedSec ?? 0;
    if (
      prevElapsedRef.current >= INTERRUPT_THRESHOLD_SEC &&
      el < INTERRUPT_THRESHOLD_SEC / 2 &&
      heldCount === prevHeldCountRef.current
    ) {
      setInterruptedUntil(Date.now() + INTERRUPT_HINT_MS);
    }
    prevElapsedRef.current = el;
  }, [currentHold?.elapsedSec, heldCount]);
  const interrupted = now < interruptedUntil && !recorded;

  const drag = baselineLocked ? topDragTip(segments) : null;

  let title: string;
  let body: React.ReactNode;
  let progress: number | null = null;
  let progressLabel: string | null = null;
  let tone: 'brand' | 'warn' | 'celebrate' = 'brand';

  if (recorded && lastHold) {
    tone = 'celebrate';
    title = `Recorded · ${labelForPosition(lastHold.positionClass as any).toUpperCase()}`;
    body = (
      <>
        Held for {Math.round(lastHold.durationSec)}s at CdA{' '}
        <Box component="span" sx={{ color: colors.brand, fontFamily: '"JetBrains Mono", monospace', fontWeight: 800 }}>
          {lastHold.cdaBest.toFixed(3)}
        </Box>
        . Change position and hold the next one to compare.
      </>
    );
    progressLabel = 'saved';
    progress = 1;
  } else if (interrupted) {
    tone = 'warn';
    title = 'You moved — hold steady';
    body =
      'The recording resets when your position changes. Get back to your last position and hold it steady for 15 seconds to record it.';
  } else if (!baselineLocked) {
    if (stalled) {
      title = 'Trouble locking baseline';
      body =
        'Ride at a steady pace and make sure your whole body is in the camera frame. If nothing moves for another minute, close and re-start the session — the calibration may need a fresh look at you.';
    } else {
      title = 'Locking your baseline';
      body =
        'Ride at a steady pace for about 10 seconds. This becomes the reference every position is measured against.';
    }
    progress = Math.max(0, Math.min(1, baselineProgress));
    progressLabel = `${Math.round(progress * 100)}%`;
  } else if (currentHold) {
    const min = currentHold.minRequiredSec;
    const elapsed = Math.min(min, Math.round(currentHold.elapsedSec));
    const cls =
      currentHold.positionClass === positionLabel?.toLowerCase() || !positionLabel
        ? currentHold.positionClass.toUpperCase()
        : (positionLabel || currentHold.positionClass).toUpperCase();
    title = `Recording · ${cls}`;
    if (elapsed < min) {
      body = (
        <>
          Hold {cls.toLowerCase()} steady for{' '}
          <Box component="span" sx={{ color: colors.brand, fontWeight: 700 }}>
            {min - elapsed} more second{min - elapsed === 1 ? '' : 's'}
          </Box>{' '}
          to record it.
        </>
      );
      progress = elapsed / min;
      progressLabel = `${elapsed} / ${min}s`;
    } else {
      body = `${cls.toLowerCase()} recorded. Change position and hold the next one to compare.`;
      progress = 1;
      progressLabel = 'saved';
    }
  } else if (positionLabel) {
    title = `Position detected · ${positionLabel}`;
    body = `Hold ${positionLabel} for 15 seconds to record it. The Aero Engineer will call out watts you're saving vs. baseline as you ride.`;
  } else if (heldCount > 0) {
    title = 'Ready';
    body =
      "You've recorded a position. Try a new one (drops, hoods, aero tuck) and hold for 15 seconds to compare.";
  } else {
    title = 'Baseline locked';
    body =
      'Get into a common riding position — drops, hoods, aero tuck — and hold for 15 seconds to record it.';
  }

  const accent = tone === 'warn' ? colors.warning : colors.brand;
  const bg = tone === 'warn' ? 'rgba(255,181,71,0.08)' : 'rgba(106,255,69,0.06)';

  return (
    <Box
      sx={{
        p: 1.75,
        borderRadius: 2,
        backgroundColor: bg,
        border: `1px solid ${accent}`,
        boxShadow:
          tone === 'celebrate'
            ? `0 0 0 3px ${colors.brand}22, 0 12px 32px ${colors.brand}18`
            : 'none',
        transition: 'box-shadow 300ms, background-color 300ms, border-color 300ms',
      }}
    >
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" spacing={1}>
        <Typography
          sx={{
            fontSize: '0.625rem',
            fontWeight: 800,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: accent,
          }}
        >
          {title}
        </Typography>
        {progressLabel && (
          <Typography
            sx={{
              fontSize: '0.65rem',
              fontFamily: '"JetBrains Mono", monospace',
              color: accent,
              fontWeight: 700,
            }}
          >
            {progressLabel}
          </Typography>
        )}
      </Stack>
      {progress != null && (
        <LinearProgress
          variant="determinate"
          value={progress * 100}
          sx={{
            mt: 0.75,
            mb: 0.75,
            height: 4,
            borderRadius: 2,
            backgroundColor: colors.surfaceElevated,
            '& .MuiLinearProgress-bar': { backgroundColor: accent },
          }}
        />
      )}
      <Typography sx={{ fontSize: '0.82rem', color: colors.text, lineHeight: 1.45, mt: 0.5 }}>
        {body}
      </Typography>

      {/* Rule-based improvement tip. Suggests the concrete change
          that'd reduce the top drag region. Only after baseline
          lock, not during recorded / interrupted flashes. */}
      {tone === 'brand' && drag && (
        <Box
          sx={{
            mt: 1.25,
            pt: 1.25,
            borderTop: `1px dashed ${colors.border}`,
          }}
        >
          <Typography
            sx={{
              fontSize: '0.6rem',
              fontWeight: 800,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: colors.data,
              mb: 0.4,
            }}
          >
            AERO ENGINEER · IMPROVE
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: colors.text, lineHeight: 1.45 }}>
            <Box component="span" sx={{ color: colors.data, fontWeight: 700 }}>
              {drag.region[0].toUpperCase() + drag.region.slice(1)} ({Math.round(drag.pct)}%)
            </Box>{' '}
            is your biggest drag source. {drag.tip}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

// Voice-narrates key session transitions so the rider doesn't have
// to look at the sidebar to know what's happening. Speaks each
// event AT MOST ONCE per session; respects a global mute flag in
// localStorage.
function SessionNarrator({
  baselineLocked,
  positionLabel,
  heldCount,
  lastHold,
  currentHold,
  topDragTip: drag,
}: {
  baselineLocked: boolean;
  positionLabel: string | null;
  heldCount: number;
  lastHold: { positionClass: string; cdaBest: number; durationSec: number } | null;
  currentHold: { positionClass: string; elapsedSec: number; minRequiredSec: number } | null;
  topDragTip: { region: string; pct: number; tip: string } | null;
}) {
  const spokenRef = useRef({
    baseline: false,
    firstPosition: false,
    lastHeldCount: 0,
    lastInterruptSpokenAt: 0,
    dragTipSpokenFor: new Set<string>(),
  });
  // Wave 20.49l — route through the shared Voice singleton so
  // SessionNarrator inherits the tab-wide dedupe (same-text 20 s,
  // global 4 s, per-category 15 s). Previous inline
  // speechSynthesis.speak had no dedupe and stacked utterances.
  const voiceRef = useRef<Voice | null>(null);
  if (voiceRef.current == null) {
    voiceRef.current = new Voice({
      wsUrl: process.env.REACT_APP_COACH_WS || 'wss://aero-ai.aero.chat',
      enabled: localStorage.getItem('aerochat.sessionVoiceMuted') !== '1',
    });
  }
  const speak = React.useCallback(
    (text: string, category: 'baseline' | 'position_change' | 'coach_beat' | 'drag_tip' | 'interrupt' = 'coach_beat') => {
      try {
        const muted = localStorage.getItem('aerochat.sessionVoiceMuted') === '1';
        voiceRef.current?.setEnabled(!muted);
        if (muted) return;
        void voiceRef.current?.speak({ text, category });
      } catch { /* browser refused */ }
    },
    [],
  );

  // Baseline lock — once.
  // Wave 20.49p — copy shortened. Baseline now locks in ~700 ms
  // (was ~3 s) because the rider was still holding from Preflight;
  // announcing "Baseline locked." as a full ceremony felt like a
  // second calibration step. Now it just says: here's what to do.
  React.useEffect(() => {
    if (baselineLocked && !spokenRef.current.baseline) {
      spokenRef.current.baseline = true;
      speak(
        "Get into your riding position — drops, hoods, or aero tuck — and hold for fifteen seconds to record it.",
        'baseline',
      );
    }
  }, [baselineLocked, speak]);

  // First position detected — once.
  React.useEffect(() => {
    if (baselineLocked && positionLabel && !spokenRef.current.firstPosition) {
      spokenRef.current.firstPosition = true;
      speak(
        `Position detected: ${positionLabel}. Hold steady for fifteen seconds and I'll record it.`,
        'position_change',
      );
    }
  }, [baselineLocked, positionLabel, speak]);

  // Position recorded — every new hold, celebrating with the
  // actual CdA number.
  React.useEffect(() => {
    if (heldCount > spokenRef.current.lastHeldCount && lastHold) {
      spokenRef.current.lastHeldCount = heldCount;
      const cls = labelForPosition(lastHold.positionClass as any);
      speak(
        `${cls} recorded. Your best CdA in that position was ${lastHold.cdaBest.toFixed(3)}. Try a new position when you're ready.`,
        'coach_beat',
      );
    }
  }, [heldCount, lastHold, speak]);

  // "You moved — hold still" nudge. Fires when a mid-hold elapsed
  // count resets from >4 s back near 0 without heldCount ticking.
  // Rate-limited to at most once every 15 s.
  const prevElapsedRef = useRef<number>(0);
  React.useEffect(() => {
    const el = currentHold?.elapsedSec ?? 0;
    if (
      prevElapsedRef.current >= INTERRUPT_THRESHOLD_SEC &&
      el < INTERRUPT_THRESHOLD_SEC / 2 &&
      heldCount === spokenRef.current.lastHeldCount
    ) {
      const now = Date.now();
      if (now - spokenRef.current.lastInterruptSpokenAt > 15_000) {
        spokenRef.current.lastInterruptSpokenAt = now;
        speak(
          "You moved. Get back to your last position and hold it steady — I'll record it in fifteen seconds.",
          'interrupt',
        );
      }
    }
    prevElapsedRef.current = el;
  }, [currentHold?.elapsedSec, heldCount, speak]);

  // Wave 20.49l — Improvement tip. Bucket is now sticky per REGION
  // (not per 5-pt pct bucket), so a drag value oscillating around a
  // boundary (42 → 43 → 42) doesn't re-fire the same tip. Voice's
  // shared text-dedupe also catches it, but pinning the ref to
  // region-only is the local-source-of-truth fix.
  React.useEffect(() => {
    if (!baselineLocked || !drag) return;
    if (spokenRef.current.dragTipSpokenFor.has(drag.region)) return;
    spokenRef.current.dragTipSpokenFor.add(drag.region);
    speak(
      `Most of your drag is coming from your ${drag.region} at ${Math.round(drag.pct)} percent. ${drag.tip}`,
      'drag_tip',
    );
  }, [baselineLocked, drag, speak]);

  return null;
}
